// Spec comparison and frame-mapping detection (F-01.4, F-01.6, F-01.8, F-01.9, C-02, C-04, C-08).

import type { FrameMapping, VideoSpec } from './types';
import { normalizeMatrix, type ColorSetup } from './color';

export type WarningLevel = 'warn' | 'info' | 'error';

export interface SpecWarning {
  level: WarningLevel;
  /** i18n key; params are interpolated by the UI. */
  key: string;
  params?: Record<string, string | number>;
}

function fpsClose(a: number, b: number, tol = 0.0002): boolean {
  return Math.abs(a - b) / Math.max(a, b) < tol;
}

/** Frame-rate "micro difference" such as 23.976 vs 24 (D-20). */
export function fpsMicroDiff(a: number, b: number): boolean {
  return !fpsClose(a, b) && Math.abs(a - b) / Math.max(a, b) < 0.01;
}

export interface MappingDetection {
  mapping: FrameMapping;
  reasonKey: string;
}

/** Automatic frame-mapping detection (F-01.8 table). */
export function detectMapping(a: VideoSpec, b: VideoSpec): MappingDetection {
  const sameish = fpsClose(a.frameRate, b.frameRate) || fpsMicroDiff(a.frameRate, b.frameRate);
  if (sameish) {
    return {
      mapping: { mode: 'one-to-one' },
      reasonKey: Math.abs(a.frameCount - b.frameCount) <= 1 ? 'mapping.reason.same' : 'mapping.reason.sameRateDiffCount',
    };
  }
  for (const r of [2, 3, 4] as const) {
    const rateOk = Math.abs(b.frameRate / a.frameRate - r) < 0.02 * r;
    const countOk = Math.abs(b.frameCount / Math.max(1, a.frameCount) - r) < 0.05 * r + 2 / Math.max(1, a.frameCount);
    if (rateOk && countOk) return { mapping: { mode: 'ratio', ratio: r }, reasonKey: 'mapping.reason.ratio' };
  }
  return { mapping: { mode: 'time' }, reasonKey: 'mapping.reason.time' };
}

/** Warnings for a single source (independent of A). */
export function sourceWarnings(s: VideoSpec): SpecWarning[] {
  const w: SpecWarning[] = [];
  if (s.decodable === false) w.push({ level: 'error', key: 'spec.warn.undecodable', params: { codec: s.codec } });
  if (s.isVariableFrameRate) w.push({ level: 'warn', key: 'spec.warn.vfr' });
  if (s.isInterlaced) w.push({ level: 'warn', key: 'spec.warn.interlaced' });
  if (s.rotation !== 0) w.push({ level: 'warn', key: 'spec.warn.rotation', params: { deg: s.rotation } });
  const trc = (s.transferCharacteristics ?? '').toLowerCase();
  if (trc === 'pq' || trc === 'smpte2084' || trc === 'hlg' || trc === 'arib-std-b67')
    w.push({ level: 'warn', key: 'spec.warn.hdr' });
  if (!normalizeMatrix(s.matrixCoefficients)) w.push({ level: 'info', key: 'spec.warn.noColorTags' });
  if (s.pixelAspectRatio !== 1) w.push({ level: 'warn', key: 'spec.warn.par', params: { par: s.pixelAspectRatio.toFixed(3) } });
  if (s.chromaSubsampling !== '4:2:0') w.push({ level: 'info', key: 'spec.warn.chroma', params: { cs: s.chromaSubsampling } });
  return w;
}

/**
 * Colour-tag reminder for a version (C-04): its effective conversion (tags, or the user's choice) differs from A's.
 * Untagged versions say what is assumed. The suggestion is A's matrix and range, applied with one click.
 */
export interface ColorTagHint {
  key: 'color.hint.untagged' | 'color.hint.differs';
  params: { a: string; b: string };
  suggest: { matrix: 'bt709' | 'bt601'; range: 'limited' | 'full' };
}

export function colorTagHint(a: ColorSetup, b: ColorSetup): ColorTagHint | null {
  if (a.matrix === b.matrix && a.range === b.range) return null;
  if (a.matrix === 'bt2020' || b.matrix === 'bt2020') return null;
  const label = (c: ColorSetup) => `${c.matrix === 'bt601' ? 'BT.601' : 'BT.709'} ${c.range === 'full' ? 'Full' : 'Limited'}`;
  return { key: b.defaulted ? 'color.hint.untagged' : 'color.hint.differs', params: { a: label(a), b: label(b) }, suggest: { matrix: a.matrix, range: a.range } };
}

/** Warnings comparing a B to A (F-01.4). */
export function compareWarnings(a: VideoSpec, b: VideoSpec, mapping: FrameMapping): SpecWarning[] {
  const w: SpecWarning[] = [];
  if (a.width !== b.width || a.height !== b.height)
    w.push({ level: 'warn', key: 'spec.warn.resolution', params: { a: `${a.width}×${a.height}`, b: `${b.width}×${b.height}` } });
  const arA = (a.width * a.pixelAspectRatio) / a.height;
  const arB = (b.width * b.pixelAspectRatio) / b.height;
  if (Math.abs(arA - arB) / arA > 0.001) w.push({ level: 'warn', key: 'spec.warn.aspect', params: { a: arA.toFixed(4), b: arB.toFixed(4) } });
  if (!fpsClose(a.frameRate, b.frameRate)) {
    if (fpsMicroDiff(a.frameRate, b.frameRate))
      w.push({ level: 'warn', key: 'spec.warn.fpsMicro', params: { a: fmtFps(a.frameRate), b: fmtFps(b.frameRate) } });
    else w.push({ level: 'warn', key: 'spec.warn.fps', params: { a: fmtFps(a.frameRate), b: fmtFps(b.frameRate) } });
  }
  const expectedCount = mapping.mode === 'ratio' ? a.frameCount * mapping.ratio : a.frameCount;
  if (mapping.mode !== 'time' && b.frameCount !== expectedCount)
    w.push({ level: 'warn', key: 'spec.warn.frameCount', params: { a: expectedCount, b: b.frameCount, diff: b.frameCount - expectedCount } });
  if (mapping.mode === 'time') w.push({ level: 'warn', key: 'spec.warn.timeMapping' });
  if (a.bitDepth !== b.bitDepth) w.push({ level: 'warn', key: 'spec.warn.bitDepth', params: { a: a.bitDepth, b: b.bitDepth } });
  if (a.chromaSubsampling !== b.chromaSubsampling)
    w.push({ level: 'warn', key: 'spec.warn.chromaDiff', params: { a: a.chromaSubsampling, b: b.chromaSubsampling } });
  const ma = normalizeMatrix(a.matrixCoefficients);
  const mb = normalizeMatrix(b.matrixCoefficients);
  if ((ma ?? 'none') !== (mb ?? 'none'))
    w.push({ level: 'warn', key: 'spec.warn.matrix', params: { a: ma ?? '—', b: mb ?? '—' } });
  if ((a.colorPrimaries ?? '') !== (b.colorPrimaries ?? '') || (a.transferCharacteristics ?? '') !== (b.transferCharacteristics ?? ''))
    w.push({ level: 'info', key: 'spec.warn.colorTags' });
  if ((a.range ?? 'limited') !== (b.range ?? 'limited'))
    w.push({ level: 'warn', key: 'spec.warn.range', params: { a: a.range ?? 'limited', b: b.range ?? 'limited' } });
  if (!!a.startTimecode !== !!b.startTimecode || (a.startTimecode && b.startTimecode && a.startTimecode.text !== b.startTimecode.text))
    w.push({ level: 'info', key: 'spec.warn.timecode', params: { a: a.startTimecode?.text ?? '—', b: b.startTimecode?.text ?? '—' } });
  if (!!a.audio !== !!b.audio) w.push({ level: 'info', key: b.audio ? 'spec.warn.audioOnlyB' : 'spec.warn.audioMissing' });
  else if (a.audio && b.audio) {
    if (a.audio.sampleRate !== b.audio.sampleRate || a.audio.channels !== b.audio.channels)
      w.push({
        level: 'info',
        key: 'spec.warn.audioFormat',
        params: { a: `${a.audio.sampleRate} Hz ${a.audio.channels}ch`, b: `${b.audio.sampleRate} Hz ${b.audio.channels}ch` },
      });
    if (Math.abs(a.audio.durationSec - b.audio.durationSec) > 0.05)
      w.push({ level: 'info', key: 'spec.warn.audioLength', params: { a: a.audio.durationSec.toFixed(2), b: b.audio.durationSec.toFixed(2) } });
  }
  return w;
}

export function fmtFps(f: number): string {
  const r = Math.round(f * 1000) / 1000;
  return Number.isInteger(r) ? `${r}` : r.toFixed(3);
}

/** Detects VFR from a list of presentation timestamps (seconds). */
export function detectVfr(timestamps: ArrayLike<number>): { vfr: boolean; fps: number } {
  const n = timestamps.length;
  if (n < 3) return { vfr: false, fps: n > 1 ? 1 / Math.max(1e-6, timestamps[1] - timestamps[0]) : 0 };
  const deltas: number[] = [];
  for (let i = 1; i < n; i++) deltas.push(timestamps[i] - timestamps[i - 1]);
  const sorted = [...deltas].sort((x, y) => x - y);
  const median = sorted[sorted.length >> 1];
  let off = 0;
  for (const d of deltas) if (Math.abs(d - median) > median * 0.05) off++;
  const avg = (timestamps[n - 1] - timestamps[0]) / (n - 1);
  const vfr = off > Math.max(2, n * 0.01);
  return { vfr, fps: 1 / (vfr ? avg : median) };
}
